import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { selectOrder } from "../../../react-reduxs/slices/orders/order_slice";
import { OrderProductStatusEnum } from "../../../enums/orders/order_status.enum";
import { useTranslation } from "react-i18next";
import { Template4CancelOrderButton } from "../templates/template-4/Template4CancelOrderButton";
import { OrderProductStatusModel } from "../../../services/order_product_statuses/models/order_product_status.model";
import { toApiImage } from "../../../funcs/string-extension";

// ─── Design tokens ────────────────────────────────────────────────────────────
const BRAND = "rgb(84,158,131)";
const BRAND_LIGHT = "rgba(84,158,131,0.08)";
const BRAND_MED = "rgba(84,158,131,0.18)";
const BRAND_BORDER = "rgba(84,158,131,0.22)";
const TEXT_DARK = "#12332a";
const TEXT_MID = "rgba(84,158,131,0.60)";

// ─── Inline styles (light theme) ─────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  trigger: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 50,
    height: 50,
    borderRadius: 14,
    background: "#ffffff",
    border: `1.5px solid ${BRAND_BORDER}`,
    cursor: "pointer",
    boxShadow: "0 2px 10px rgba(84,158,131,0.18), 0 1px 3px rgba(0,0,0,0.05)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
  },
  triggerIcon: {
    fontSize: 25,
    color: BRAND,
    lineHeight: 1,
  },
  badge: {
    position: "absolute",
    top: -6,
    right: -6,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    background: "linear-gradient(135deg, #f97316, #ef4444)",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingInline: 4,
    fontFamily: "'DM Sans', sans-serif",
    letterSpacing: "0.02em",
  },
  panel: {
    position: "absolute",
    bottom: 56,
    right: 0,
    width: 320,
    background: "#ffffff",
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: 20,
    boxShadow: "0 12px 40px rgba(84,158,131,0.14), 0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
    fontFamily: "'DM Sans', sans-serif",
    zIndex: 999,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 18px 10px",
    background: BRAND_LIGHT,
    borderBottom: `1px solid ${BRAND_BORDER}`,
  },
  headerTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
    color: BRAND,
  },
  headerSub: {
    fontSize: 11,
    color: TEXT_MID,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    fontWeight: 600,
  },
  list: {
    maxHeight: 320,
    overflowY: "auto" as const,
    padding: "8px 0",
    scrollbarWidth: "none" as const,
    background: "#f9fffd",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "7px 7px",
    transition: "background 0.15s ease",
    cursor: "default",
  },
  img: {
    width: 54,
    height: 54,
    borderRadius: 12,
    objectFit: "cover" as const,
    flexShrink: 0,
    border: `1px solid ${BRAND_BORDER}`,
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontSize: 14,
    fontWeight: 600,
    color: TEXT_DARK,
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.3,
  },
  productPrice: {
    fontSize: 13,
    color: BRAND,
    fontWeight: 700,
    marginTop: 2,
    fontVariantNumeric: "tabular-nums",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase" as const,
  },
  statusDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    flexShrink: 0,
  },
  amountBadge: {
    flexShrink: 0,
    background: BRAND_MED,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND,
    borderRadius: 10,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textAlign: "center" as const,
    minWidth: 42,
  },
  divider: {
    height: 1,
    background: BRAND_BORDER,
    margin: "0 16px",
  },
  footer: {
    padding: "10px 10px 10px",
    borderTop: `1px solid ${BRAND_BORDER}`,
    background: BRAND_LIGHT,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerLabel: {
    fontSize: 12,
    color: TEXT_MID,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    fontWeight: 600,
  },
  footerTotal: {
    fontSize: 16,
    fontWeight: 800,
    color: BRAND,
    fontVariantNumeric: "tabular-nums",
  },
  wrapper: {
    position: "relative",
    display: "inline-block",
  },
  empty: {
    padding: "24px 18px",
    textAlign: "center" as const,
    color: TEXT_MID,
    fontSize: 13,
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
interface CompanyTableOrderPageProps {
  color?: string;
  orderProductStatusList: OrderProductStatusModel[];
}

const CompanyTableOrderPage: React.FC<CompanyTableOrderPageProps> = ({
  color = BRAND,
  orderProductStatusList,
}) => {
  const { t } = useTranslation();
  const order = useSelector(selectOrder);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const totalPrice = useCallback(() => {
    let total = 0;
    order?.orderProductList?.forEach((el) => {
      total += el.amount * el.menuProductPrice;
    });
    return total;
  }, [order?.orderProductList]);

  const pendingCount =
    order?.orderProductList?.filter(
      (x) => x.status.text !== OrderProductStatusEnum.pendingApproval,
    ).length ?? 0;

  const list = order?.orderProductList ?? [];

  return (
    <div style={styles.wrapper} ref={panelRef}>
      {/* ── Trigger Button ── */}
      <div
        style={{
          ...styles.trigger,
          transform: open ? "scale(0.94)" : "scale(1)",
          boxShadow: open
            ? "0 1px 6px rgba(84,158,131,0.12)"
            : (styles.trigger.boxShadow as string),
        }}
        onClick={() => setOpen((v) => !v)}
        title="Siparişleri Görüntüle"
      >
        <i
          className="fa-solid fa-plate-utensils"
          style={{ ...styles.triggerIcon, color }}
        />
        {pendingCount > 0 && (
          <span
            style={{
              ...styles.badge,
              color: "white",
              background: color,
            }}
          >
            {pendingCount}
          </span>
        )}
      </div>

      {/* ── Dropdown Panel ── */}
      {open && (
        <div style={styles.panel}>
          {/* Header */}
          <div style={styles.header}>
            <span style={styles.headerTitle}>Sipariş Detayı</span>
            <span style={styles.headerSub}>{list.length} Ürün</span>
          </div>

          {/* List */}
          <div style={styles.list}>
            {list.length === 0 ? (
              <div style={styles.empty}>Henüz sipariş yok.</div>
            ) : (
              list.map((x, i) => (
                <React.Fragment key={String(x.id)}>
                  <div
                    style={{
                      ...styles.row,
                      background:
                        hovered === String(x.id) ? BRAND_LIGHT : "transparent",
                    }}
                    onMouseEnter={() => setHovered(String(x.id))}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <img
                      src={toApiImage(x.menuProductPhoto, "product.png")}
                      alt={x.menuProductName}
                      style={styles.img}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.background =
                          BRAND_LIGHT;
                      }}
                    />
                    <div style={styles.textBlock}>
                      <div style={styles.productName}>{x.menuProductName}</div>
                      <div style={styles.productPrice}>
                        {x.menuProductPrice} ₺
                      </div>
                      <div
                        style={{
                          ...styles.statusPill,
                          background: x.status.color + "22",
                          color: x.status.color,
                        }}
                      >
                        <span
                          style={{
                            ...styles.statusDot,
                            background: x.status.color,
                          }}
                        />
                        {t("common." + x.status.name)}
                      </div>
                    </div>
                    <div style={styles.amountBadge}>×{x.amount}</div>
                    {x.status.name !== OrderProductStatusEnum.cancelled &&
                      x.status.name !== OrderProductStatusEnum.delivered &&
                      x.status.name !== OrderProductStatusEnum.ready &&
                      x.status.name !== OrderProductStatusEnum.outOfStock && (
                        <Template4CancelOrderButton
                          order={order?.order}
                          orderProduct={x}
                          orderProductStatusList={orderProductStatusList}
                        />
                      )}
                  </div>
                  {i < list.length - 1 && <div style={styles.divider} />}
                </React.Fragment>
              ))
            )}
          </div>

          {/* Footer */}
          {list.length > 0 && (
            <div style={styles.footer}>
              <span style={styles.footerLabel}>Toplam Tutar</span>
              <span style={styles.footerTotal}>{totalPrice()} ₺</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyTableOrderPage;
